/**
 * /brands/:brandId — 品牌详情页（Phase 4 §4.1）
 *
 * 元素：
 * - 面包屑 + 标题 + 风险等级 Tag
 * - 4 KPI: 平台综合分 / 本周投诉 / 30 天投诉 / 厂家回复率
 * - 12 周投诉趋势折线（从 /api/v2/overview 的 complaint_trend 取本 brand series）
 * - 风险雷达 6 维（用 risk_score 6 个 component 反算）
 * - 本品牌车型表（从 /api/v2/models/weekly?brand_id= 拉）
 * - 正/负面 drivers (KeywordChip)
 *
 * 失败降级：banner + mock。
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Skeleton, Tag, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';

import DriverChip from '@/components/DriverChip';
import DriverWordcloud from '@/components/DriverWordcloud';
import MockBanner from '@/components/MockBanner';
import Panel from '@/components/Panel';
import RiskTag from '@/components/RiskTag';
import StatCard from '@/components/StatCard';
import {
  DATA_NOT_READY_HINT,
  isWeekDataReady,
  modelsFromEnvelope,
  type ModelsWeeklyEnvelope,
  type WeekStatus,
} from '@/lib/weekData';
import { api } from '@/lib/api';
import { useBreakpoint } from '@/lib/breakpoints';
import { buildBrandRiskRadar, type RiskComponent } from '@/lib/buildBrandRiskRadar';
import { useWeekStore } from '@/stores/weekStore';

interface BrandWeeklyMetrics {
  platform_avg_score: number | null;
  complaint_count_7d: number;
  complaint_count_30d: number;
  complaint_count_wow: number | null;
  recall_count_ytd: number;
  sentiment_score: number | null;
  negative_sentiment_ratio: number | null;
  response_rate: number | null;
  risk_score: number;
  risk_level: '低' | '中' | '高' | '重大';
  key_positive_drivers: string[] | null;
  key_negative_drivers: string[] | null;
  data_sources: string[] | null;
  risk_components: RiskComponent[];
}

interface BrandWeeklyResp {
  brand_id: string;
  brand_name: string;
  brand_type: string | null;
  parent_group: string | null;
  logo_url: string | null;
  week: string;
  week_status?: WeekStatus; // PR 9.2m: 严格 readiness(W23 红线)
  metrics: BrandWeeklyMetrics | null;
}

interface ModelMetricsRow {
  model_id: string;
  brand_id: string;
  model_name: string;
  segment: string | null;
  powertrain_type: string | null;
  week: string;
  metrics: {
    avg_score: number | null;
    complaint_count: number;
    complaint_count_wow: number | null;
    risk_score: number;
    risk_level: '低' | '中' | '高' | '重大';
  } | null;
}

interface OverviewResp {
  complaint_trend: {
    weeks: string[];
    series: Array<{ brand: string; color: string; data: number[] }>;
  };
}

export default function BrandDetailPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { week, history } = useWeekStore();
  const { isMobile } = useBreakpoint();
  const chartHeight = isMobile ? 240 : 280;
  const [pdfLoading, setPdfLoading] = useState(false);

  // 9.2h W3(Issue 4): 导出高管周报 PDF
  const handleExportPdf = async () => {
    if (!brandId) return;
    setPdfLoading(true);
    try {
      const res = await api.get(`/brands/${brandId}/weekly-report.pdf`, {
        params: { week },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${brandId}-${week}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      message.error('周报 PDF 导出失败');
    } finally {
      setPdfLoading(false);
    }
  };

  const brandQ = useQuery({
    queryKey: ['brandWeekly', brandId, week],
    queryFn: async () => (await api.get<BrandWeeklyResp>(`/brands/${brandId}/weekly`, { params: { week } })).data,
    retry: false,
    enabled: Boolean(brandId),
  });

  const modelsQ = useQuery({
    queryKey: ['brandModels', brandId, week],
    queryFn: async () =>
      // PR 9.2m carry-forward 1: envelope {week, week_status, models}(旧 array 作废)
      modelsFromEnvelope(
        (
          await api.get<ModelsWeeklyEnvelope<ModelMetricsRow>>('/models/weekly', {
            params: { week, brand_id: brandId },
          })
        ).data,
      ),
    retry: false,
    enabled: Boolean(brandId),
  });

  const trendQ = useQuery({
    queryKey: ['overviewTrend', week],
    queryFn: async () => (await api.get<OverviewResp>('/overview', { params: { week } })).data,
    retry: false,
  });

  const data = brandQ.data;
  const models = modelsQ.data ?? [];
  const usingMock = !brandQ.data && !brandQ.isLoading;

  if (brandQ.isLoading) return <Skeleton active paragraph={{ rows: 12 }} />;
  if (!data && !usingMock) return null;

  const metrics = data?.metrics;
  const brandName = data?.brand_name ?? brandId ?? '';
  // PR 9.2m: 本周是否就绪(W23 红线:not-ready 不显硬 0/环比/风险分)。
  const weekReady = isWeekDataReady(data?.week_status);

  // 12 周折线（从 overview trend 抓本 brand 的 series）
  const myTrendSeries = trendQ.data?.complaint_trend.series.find((s) => s.brand === brandName);
  const trendWeeks = trendQ.data?.complaint_trend.weeks ?? [];

  // 风险雷达 6 维 — 后端 /brands/{id}/weekly 暴露的真实 risk formula components
  const riskComponents = metrics?.risk_components ?? [];

  return (
    <div className="space-y-5">
      {usingMock && <MockBanner message="品牌详情接口暂不可用 — 显示占位 mock。" />}

      {/* 面包屑 + 标题 */}
      <div>
        <div className="text-[12px] text-slate-500 mb-1">
          <Link to="/brands" className="hover:underline">
            品牌列表
          </Link>
          <span className="mx-1.5">/</span>
          <span>{brandName}</span>
        </div>
        <div className="flex items-center gap-2 lg:gap-3 flex-wrap">
          <h1 className="text-[18px] lg:text-[22px] font-semibold text-slate-900 m-0">{brandName}</h1>
          {/* PR 9.2m microfix: header RiskTag 仅 ready 周显(W23 红线,不显硬 risk 值) */}
          {weekReady && metrics && <RiskTag level={metrics.risk_level} score={metrics.risk_score} />}
          <span className="text-[12px] text-slate-500">· {week}</span>
          <Button
            icon={<DownloadOutlined />}
            size="small"
            loading={pdfLoading}
            onClick={handleExportPdf}
            className="ml-auto lg:ml-2"
          >
            导出周报 PDF
          </Button>
        </div>
        {data?.brand_type && (
          <p className="text-[12px] text-slate-500 mt-1 mb-0">
            {data.brand_type} · {data.parent_group}
          </p>
        )}
      </div>

      {/* 4 KPI — PR 9.2m: not-ready 周整组 gate(W23 红线,不显硬 0/-100%) */}
      {!weekReady ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-8 text-center text-[13px] text-slate-500">
          {DATA_NOT_READY_HINT}
        </div>
      ) : (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <StatCard
          title="平台综合分"
          value={metrics?.platform_avg_score?.toFixed(2) ?? '—'}
          hint="懂车帝/易车/汽车之家/车质网 加权"
        />
        <StatCard
          title="本周投诉"
          value={metrics?.complaint_count_7d.toLocaleString() ?? '0'}
          trend={
            metrics?.complaint_count_wow !== null && metrics?.complaint_count_wow !== undefined
              ? `${metrics.complaint_count_wow > 0 ? '↑' : '↓'} ${Math.abs(metrics.complaint_count_wow * 100).toFixed(1)}%`
              : undefined
          }
          trendType={metrics?.complaint_count_wow && metrics.complaint_count_wow > 0 ? 'up' : 'down'}
        />
        <StatCard
          title="30 天投诉"
          value={metrics?.complaint_count_30d.toLocaleString() ?? '0'}
          hint="近 30 天合计"
        />
        <StatCard
          title="厂家回复率"
          value={metrics?.response_rate !== null && metrics?.response_rate !== undefined ? `${(metrics.response_rate * 100).toFixed(1)}%` : '—'}
          hint="已回复 / 总投诉"
        />
      </div>
      )}

      {/* 趋势 + 雷达 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Panel title="12 周投诉趋势">
            {/* 9.2g: 近 8 周完整度（消费 /weeks/history,缺周待补采） */}
            {history.length > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-1.5">
                <span className="text-[12px] text-slate-400">近 8 周:</span>
                {history.map((item) => (
                  <Tag
                    key={item.week}
                    color={item.status === 'ready' ? 'green' : 'default'}
                    className="!text-[11px] !mr-0"
                  >
                    {item.week.slice(-3)}
                    {item.status !== 'ready' && ' 待补采'}
                  </Tag>
                ))}
              </div>
            )}
            {/* PR 9.2m microfix: not-ready 周不渲趋势 chart(W23 红线) */}
            {weekReady && myTrendSeries ? (
              <ReactECharts option={buildTrendOption(trendWeeks, myTrendSeries.data, myTrendSeries.color, isMobile)} style={{ height: chartHeight }} />
            ) : (
              <div className="text-[12px] text-slate-400 py-12 text-center">{DATA_NOT_READY_HINT}</div>
            )}
          </Panel>
        </div>
        <Panel title="风险评分雷达">
          {/* PR 9.2m microfix: not-ready 周不渲风险雷达/硬 risk 维(W23 红线) */}
          {!weekReady ? (
            <div className="text-[12px] text-slate-400 py-12 text-center">{DATA_NOT_READY_HINT}</div>
          ) : riskComponents.length > 0 ? (
            <>
              <ReactECharts option={buildBrandRiskRadar(riskComponents)} style={{ height: chartHeight }} />
              {/* 6 维 breakdown table — canvas 不可读, 给用户精确得分 + 权重 */}
              <div className="mt-2 border-t border-slate-100 pt-2">
                <div className="grid grid-cols-12 gap-2 text-[11px] font-semibold text-slate-500 px-1 pb-1">
                  <div className="col-span-6">风险维度</div>
                  <div className="col-span-3 text-right">得分</div>
                  <div className="col-span-3 text-right">权重</div>
                </div>
                {riskComponents.map((c) => (
                  <div
                    key={c.key}
                    className="grid grid-cols-12 gap-2 text-[12px] text-slate-700 px-1 py-1 border-b border-slate-50"
                  >
                    <div className="col-span-6">{c.label}</div>
                    <div className="col-span-3 text-right">{c.value.toFixed(1)}</div>
                    <div className="col-span-3 text-right text-slate-400">{(c.weight * 100).toFixed(0)}%</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-[12px] text-slate-400 py-12 text-center">暂无风险分量数据</div>
          )}
        </Panel>
      </div>

      {/* 本品牌车型表 — 9.2f: mobile 横滚保最小宽度防 5 列挤压 */}
      <Panel title={`${brandName} 车型表现`} extra={<span className="text-[11px] text-slate-400">共 {models.length} 款</span>}>
        {/* PR 9.2m microfix v2: not-ready 周不显车型硬 0/风险等级(W23 红线) */}
        {!weekReady ? (
          <div className="text-[12px] text-slate-400 py-12 text-center">{DATA_NOT_READY_HINT}</div>
        ) : (
        <div className="overflow-x-auto">
        <div className="min-w-[460px] lg:min-w-0">
        <div className="grid grid-cols-12 gap-2 text-[12px] font-semibold text-slate-600 px-2 py-2 border-b border-slate-100">
          <div className="col-span-3">车型</div>
          <div className="col-span-2 text-right">综合评分</div>
          <div className="col-span-2 text-right">本周投诉</div>
          <div className="col-span-2 text-right">环比</div>
          <div className="col-span-3 text-right">风险等级</div>
        </div>
        {models.length === 0 && (
          <div className="text-[12px] text-slate-400 py-6 text-center">本品牌暂无车型数据</div>
        )}
        {models.map((m) => (
          <div
            key={m.model_id}
            className="grid grid-cols-12 gap-2 text-[12.5px] text-slate-700 px-2 py-2 border-b border-slate-50 hover:bg-slate-50"
          >
            <div className="col-span-3">
              <Link to={`/models/${m.model_id}`} className="text-slate-900 hover:underline">
                {m.model_name}
              </Link>
            </div>
            <div className="col-span-2 text-right">{m.metrics?.avg_score?.toFixed(2) ?? '—'}</div>
            <div className="col-span-2 text-right">{m.metrics?.complaint_count ?? 0}</div>
            <div className="col-span-2 text-right">
              {m.metrics?.complaint_count_wow !== null && m.metrics?.complaint_count_wow !== undefined
                ? `${m.metrics.complaint_count_wow > 0 ? '+' : ''}${(m.metrics.complaint_count_wow * 100).toFixed(1)}%`
                : '—'}
            </div>
            <div className="col-span-3 text-right">
              {m.metrics ? <RiskTag level={m.metrics.risk_level} /> : '—'}
            </div>
          </div>
        ))}
        </div>
        </div>
        )}
      </Panel>

      {/* drivers — PR 9.2m microfix(3.4-b G5 prod 残漏): not-ready 周不渲任何驱动分析
          (正面/负面驱动 chip + 驱动词云 canvas),W23 红线 —— prod 真实 driver 数据下泄漏。 */}
      {!weekReady ? (
        <Panel title="驱动分析">
          <div className="text-[12px] text-slate-400 py-12 text-center">{DATA_NOT_READY_HINT}</div>
        </Panel>
      ) : (
      <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Panel title="正面驱动">
          <div className="flex gap-2 flex-wrap">
            {(metrics?.key_positive_drivers ?? []).length === 0 && (
              <span className="text-[12px] text-slate-400">暂无正面关键词</span>
            )}
            {(metrics?.key_positive_drivers ?? []).map((w) => (
              <DriverChip key={w} word={w} polarity="positive" brandId={brandId ?? ''} week={week} />
            ))}
          </div>
        </Panel>
        <Panel title="负面驱动">
          <div className="flex gap-2 flex-wrap">
            {(metrics?.key_negative_drivers ?? []).length === 0 && (
              <span className="text-[12px] text-slate-400">暂无负面关键词</span>
            )}
            {(metrics?.key_negative_drivers ?? []).map((w) => (
              <DriverChip key={w} word={w} polarity="negative" brandId={brandId ?? ''} week={week} />
            ))}
          </div>
        </Panel>
      </div>

      {/* 9.2h W4(Issue 2): 驱动词云 */}
      <Panel title="驱动词云（正面绿 / 负面红）">
        <DriverWordcloud brandId={brandId ?? ''} week={week} height={isMobile ? 240 : 300} />
      </Panel>
      </>
      )}
    </div>
  );
}

function buildTrendOption(weeks: string[], series: number[], color: string, isMobile = false): EChartsOption {
  return {
    grid: { left: isMobile ? 32 : 40, right: 16, top: 16, bottom: isMobile ? 56 : 50 },
    tooltip: { trigger: 'axis', confine: true },
    xAxis: { type: 'category', data: weeks, axisLabel: { fontSize: isMobile ? 9 : 10, color: '#64748B', rotate: isMobile ? 45 : 0 } },
    yAxis: { type: 'value', axisLabel: { fontSize: isMobile ? 9 : 10, color: '#64748B' } },
    series: [
      {
        type: 'line',
        name: '投诉量',
        data: series,
        itemStyle: { color },
        areaStyle: { color, opacity: 0.1 },
        smooth: true,
      },
    ],
  };
}
